import type { AnswerConditionOperator, Question, QuestionInputType } from '@dashboard/types/config'
import countries from '@dashboard/data/countries'

const presenceOperators: AnswerConditionOperator[] = ['has answered', 'has not answered']
const numericOperators: AnswerConditionOperator[] = [
  'equals',
  'does not equal',
  'greater than',
  'greater than or equal to',
  'less than',
  'less than or equal to',
  'between'
]
const textOperators: AnswerConditionOperator[] = ['equals', 'contains', 'starts with', 'is empty', 'is not empty']
const dateOperators: AnswerConditionOperator[] = [
  'before',
  'after',
  'on',
  'between',
  'in the past',
  'in the future',
  'within the next days',
  'within the last days'
]
const countryOperators: AnswerConditionOperator[] = ['is', 'is not', 'is any of']
const partCountryOperators: AnswerConditionOperator[] = ['country is', 'country is not', 'country is any of']
const domainOperators: AnswerConditionOperator[] = ['domain is', 'domain ends with']
const emailOperators: AnswerConditionOperator[] = [
  ...domainOperators,
  'domain is not',
  'domain is any of',
  'is a business email',
  'is a personal email'
]
const nameOperators: AnswerConditionOperator[] = [
  'first name equals',
  'first name contains',
  'has last name',
  'has no last name'
]
const addressOperators: AnswerConditionOperator[] = [
  ...partCountryOperators,
  'region equals',
  'city equals',
  'postal code starts with'
]
const allOperators = [
  ...presenceOperators,
  ...numericOperators,
  ...textOperators,
  ...dateOperators,
  ...countryOperators,
  ...partCountryOperators,
  ...emailOperators,
  ...nameOperators,
  ...addressOperators
]
const countryCodes = new Set(countries.map((country) => country.value))
const relativeDaysOperators = ['within the next days', 'within the last days']
const partTextOperators = [
  'region equals',
  'city equals',
  'postal code starts with',
  'first name equals',
  'first name contains'
]
const valueFreeOperators = [
  ...presenceOperators,
  'is empty',
  'is not empty',
  'is a business email',
  'is a personal email',
  'has last name',
  'has no last name',
  'in the past',
  'in the future'
]

export function answerValueType(
  type: QuestionInputType | null | undefined,
  operator?: string
): 'number' | 'date' | 'country' | 'text' | 'presence' {
  if (operator?.startsWith('country ')) {
    return 'country'
  }
  if (operator && relativeDaysOperators.includes(operator)) {
    return 'number'
  }
  if (operator?.startsWith('domain ') || (operator && partTextOperators.includes(operator))) {
    return 'text'
  }
  if (type === 'number' || type === 'rating' || type === 'nps') {
    return 'number'
  }
  if (type === 'date' || type === 'country') {
    return type
  }
  if (type === 'text' || type === 'textarea' || type === 'url') {
    return 'text'
  }
  return 'presence'
}

export function answerOperators(
  type: QuestionInputType | null | undefined,
  question?: Question
): AnswerConditionOperator[] {
  const selectedType = question?.inputType ?? type
  let comparisons: AnswerConditionOperator[]
  switch (selectedType) {
    case 'number':
    case 'rating':
    case 'nps':
      comparisons = numericOperators
      break
    case 'date':
      comparisons = dateOperators
      break
    case 'country':
      comparisons = countryOperators
      break
    case 'text':
    case 'textarea':
      comparisons = textOperators
      break
    case 'url':
      comparisons = [...textOperators, ...domainOperators]
      break
    case 'phone':
      comparisons = partCountryOperators
      break
    case 'email':
      comparisons = emailOperators
      break
    case 'name':
      comparisons = nameOperators
      break
    case 'address':
      comparisons = addressOperators
      break
    case null:
    case undefined:
      comparisons = []
  }
  return [...comparisons, ...presenceOperators]
}

export function answerNeedsValue(operator: unknown): boolean {
  return !valueFreeOperators.includes(String(operator))
}

const isDate = (value: unknown): value is string => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false
  }
  const date = new Date(`${value}T00:00:00Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
}
const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)
const isCountry = (value: unknown): value is string => typeof value === 'string' && countryCodes.has(value)
const isDomain = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length <= 253 &&
  /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(value)

/** With a catalog, enforce the selected question's type and part-level contact filters. */
export function validateAnswerCondition(
  condition: { questionId?: unknown; operator?: unknown; value?: unknown; $type?: unknown },
  questions?: readonly Question[]
): { field: string; message: string }[] {
  const errors: { field: string; message: string }[] = []
  if (typeof condition.questionId !== 'string' || !/^raq_\w+$/.test(condition.questionId)) {
    errors.push({ field: 'questionId', message: 'Select an input question.' })
  }
  const question = questions?.find((item) => item.id === condition.questionId && item.displayType === 'input')
  if (questions && !question && errors.length === 0) {
    errors.push({ field: 'questionId', message: 'This input question is no longer available.' })
  }
  const operator = condition.operator
  const allowed = question ? answerOperators(question.inputType, question) : allOperators
  if (typeof operator !== 'string' || !allowed.some((candidate) => candidate === operator)) {
    errors.push({ field: 'operator', message: 'Select an operator available for this question type.' })
    return errors
  }
  for (const key of Object.keys(condition)) {
    if (!['$source', 'questionId', 'operator', 'value'].includes(key)) {
      errors.push({
        field: key,
        message: 'Answer conditions use only $source, questionId, operator and optional value.'
      })
    }
  }
  if (!answerNeedsValue(operator)) {
    if (condition.value !== undefined) {
      errors.push({ field: 'value', message: 'This operator does not take a value.' })
    }
    return errors
  }

  const type = question ? answerValueType(question.inputType, operator) : undefined
  const value = condition.value
  let valid = false
  if (relativeDaysOperators.includes(operator)) {
    valid = isNumber(value) && Number.isSafeInteger(value) && value >= 0
  } else if (operator.startsWith('domain ')) {
    valid =
      operator === 'domain is any of'
        ? Array.isArray(value) && value.length > 0 && value.every(isDomain)
        : isDomain(value)
  } else if (partTextOperators.includes(operator)) {
    valid = typeof value === 'string' && value.trim().length > 0
  } else if (operator === 'between') {
    const [minimum, maximum] = Array.isArray(value) ? value : []
    valid =
      Array.isArray(value) &&
      value.length === 2 &&
      ((type !== 'date' && isNumber(minimum) && isNumber(maximum) && minimum <= maximum) ||
        (type !== 'number' && isDate(minimum) && isDate(maximum) && minimum <= maximum))
  } else if (operator === 'is any of' || operator === 'country is any of') {
    valid = Array.isArray(value) && value.length > 0 && value.every(isCountry)
  } else if (type === 'country' || operator === 'is' || operator === 'is not' || operator.startsWith('country ')) {
    valid = isCountry(value)
  } else if (
    type === 'number' ||
    (numericOperators.some((candidate) => candidate === operator) && operator !== 'equals')
  ) {
    valid = isNumber(value)
  } else if (type === 'date' || dateOperators.some((candidate) => candidate === operator)) {
    valid = isDate(value)
  } else {
    valid = typeof value === 'string' || (type === undefined && operator === 'equals' && isNumber(value))
  }
  if (!valid) {
    errors.push({
      field: 'value',
      message:
        operator === 'between' ? 'Enter a valid minimum and maximum in ascending order.' : 'Enter a valid answer value.'
    })
  }
  return errors
}
